import moment from 'moment';
import { useMemo } from 'react';
import useOneListing from 'hooks/useOneListing';
import { DATE_FORMAT } from 'utils';
import useCancellationPolicy from 'hooks/useCancellationPolicy';
import useEventSessions, {
  PublicSessionsQueryParams,
} from './useEventSessions';

interface Props {
  brandId: string;
  eventId?: string;
  date?: string;
  latest?: boolean;
}

const useEventBookingSessionSelectedData = ({
  eventId,
  date,
  latest,
}: Props) => {
  // 1 - GET EVENT FROM API
  const { listing: event, isLoading: isLoadingEvent } = useOneListing(
    eventId,
    latest,
  );

  // 2 - GET SESSION FROM API
  const sortedDates = useMemo(() => {
    if (!event?.dates?.length) return [];
    return [...event.dates].sort((a, b) => moment(a).diff(moment(b)));
  }, [event?.dates]);

  const nextScheduledDate = !sortedDates.length
    ? []
    : sortedDates.find((date) =>
        moment(date).startOf('day').isSameOrAfter(moment().startOf('day')),
      );

  const lastScheduledDate = !sortedDates.length
    ? []
    : sortedDates[sortedDates.length - 1];

  const eventSessionsProps: PublicSessionsQueryParams = { latest };
  const daySessionsProps: PublicSessionsQueryParams = { latest };
  if (eventId && event) {
    eventSessionsProps.eventId = eventId;
    eventSessionsProps.from = moment(nextScheduledDate || moment())
      .subtract(1, 'day')
      .format(DATE_FORMAT);
    eventSessionsProps.to = moment(lastScheduledDate || eventSessionsProps.from)
      .add(1, 'day')
      .format(DATE_FORMAT);

    daySessionsProps.eventId = eventId;
    if (date) {
      daySessionsProps.from = date;
      daySessionsProps.to = date;
    }
  }
  const { sessions: eventSessions, isLoading: isLoadingEventSessions } =
    useEventSessions(eventSessionsProps);

  const { sessions: daySessions, isLoading: isLoadingDaySessions } =
    useEventSessions(daySessionsProps);

  // 4 - GET CANCELLATION POLICY FROM EVENT OR BRAND SETTINGS
  const { cancellationPolicy, isLoading: isLoadingCancellationPolicy } =
    useCancellationPolicy(event?.cancellationPolicyId);

  return {
    isLoading:
      isLoadingEvent ||
      isLoadingEventSessions ||
      isLoadingDaySessions ||
      isLoadingCancellationPolicy,
    isLoadingEvent,
    isLoadingEventSessions,
    isLoadingDaySessions,
    isLoadingCancellationPolicy,
    event,
    eventSessions,
    daySessions,
    cancellationPolicy,
  };
};

export default useEventBookingSessionSelectedData;
